import React, { useState, useEffect } from "react";
import qs from "query-string";
import { useSelector } from "react-redux";

import { Language, LegalEntityRegistrationStorage } from "../../utils";
import { Page, Confirm } from "../../components";

import makeClasses from "./styles";
import clsx from "clsx";

import appLogo from "../../static/images/logo.svg";
import HelpIcon from "../../static/images/icons/help.svg";

import Disclaimer from "./steps/Disclaimer";
import StepOne from "./steps/StepOne";
import StepTwo from "./steps/StepTwo";
import StepThree from "./steps/StepThree";
import StepFour from "./steps/StepFour";
import StepFive from "./steps/StepFive";
import StepSix from "./steps/StepSix";
import StepSeven from "./steps/StepSeven";
import StepEight from "./steps/StepEight";
import StepNine from "./steps/StepNine";
import StepTen from "./steps/StepTen";
import StepEleven from "./steps/StepEleven";

const LegalEntityRegistrationScreen = ({ history }) => {
  const classes = makeClasses();
  const [showHelp, setShowHelp] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  const { languageTexts } = useSelector((state) => ({
    languageTexts: state.language.texts || {},
  }));

  const i18n = Language(languageTexts);

  const searchParams = qs.parse(history.location.search);

  const [initialParams] = useState(() => {
    const params = qs.parse(history.location.search);
    const { step, data, requiresPosExemption, ...restParams } = params;
    return restParams;
  });

  const [affiliationCode, setAffiliationCode] = useState(
    qs.parse(history?.location?.search)?.code ?? null,
  );
  const [traceId, setTraceId] = useState(() =>
    LegalEntityRegistrationStorage.getTraceId(),
  );
  const [requiresPosExemption, setRequiresPosExemption] = useState(() =>
    LegalEntityRegistrationStorage.getRequiresPosExemption(),
  );
  const [formData, setFormData] = useState({
    data: searchParams.data || "",
    email: "",
    phone: "",
    countryId: null,
  });
  const [stepOneFields, setStepOneFields] = useState(null);

  const [registerStep, setRegisterStep] = useState(() => {
    return searchParams.step ? parseInt(searchParams.step) : 1;
  });
  const [maxStepReached, setMaxStepReached] = useState(() => {
    return LegalEntityRegistrationStorage.getMaxStep();
  });

  const updateMaxStepReached = (step) => {
    const newMax = Math.max(maxStepReached, step);
    setMaxStepReached(newMax);
    LegalEntityRegistrationStorage.setMaxStep(newMax);
  };

  const updateUrlWithStep = (step, data, requiresPosExemption) => {
    const currentParams = qs.parse(history.location.search);
    const params = {
      ...currentParams,
      step,
      code: affiliationCode,
    };

    if (data !== undefined) {
      params.data = data;
    }

    if (requiresPosExemption !== undefined) {
      params.requiresPosExemption = requiresPosExemption;
    }

    history.push({
      search: qs.stringify(params),
    });
  };

  useEffect(() => {
    if (searchParams.result) {
      if (searchParams.result === "success") {
        setRegisterStep(12);
        updateMaxStepReached(12);
      } else if (
        searchParams.result === "error" ||
        searchParams.result === "expired"
      ) {
        setRegisterStep(11);
        updateMaxStepReached(11);
      } else {
        setRegisterStep(1);
      }
    }
  }, [searchParams.result]);

  useEffect(() => {
    if (searchParams.step && !searchParams.result) {
      const requestedStep = parseInt(searchParams.step);
      const isDev = process.env.NODE_ENV === "development";

      if (
        isDev ||
        requestedStep === maxStepReached ||
        requestedStep === maxStepReached + 1 ||
        requestedStep === 1
      ) {
        setRegisterStep(requestedStep);
        if (isDev) {
          updateMaxStepReached(requestedStep);
        } else if (requestedStep === 1 && maxStepReached > 1) {
          setMaxStepReached(1);
          LegalEntityRegistrationStorage.setMaxStep(1);
        } else if (requestedStep === maxStepReached + 1) {
          setMaxStepReached(requestedStep);
          LegalEntityRegistrationStorage.setMaxStep(requestedStep);
        }
      } else {
        updateUrlWithStep(
          maxStepReached,
          searchParams.data,
          searchParams.requiresPosExemption,
        );
      }
    }
    if (searchParams.data) {
      setFormData(searchParams);
    }
    const savedData = LegalEntityRegistrationStorage.getStep1Data();
    if (
      savedData &&
      (savedData.email || savedData.phone || savedData.countryId)
    ) {
      setFormData((prev) => ({
        ...prev,
        email: savedData.email || prev.email,
        phone: savedData.phone || prev.phone,
        countryId: savedData.countryId || prev.countryId,
      }));
    }
  }, [searchParams.step, searchParams.data]);

  const _toggleShowHelp = () => {
    setShowHelp(!showHelp);
  };

  useEffect(() => {
    if (
      registerStep === 1 &&
      !searchParams.data &&
      process.env.NODE_ENV !== "development"
    ) {
      LegalEntityRegistrationStorage.clearAll();
      setFormData({ data: "", email: "", phone: "" });
    }
  }, [registerStep]);

  useEffect(() => {
    let blockedLocation = null;
    let savedSearch = null;

    const unblock = history.block((location, action) => {
      if (action === "POP") {
        savedSearch = history.location.search;

        if (!location.pathname.includes("registro-empresa")) {
          blockedLocation = location;
          setShowExitConfirm(true);
          return false;
        }

        if (registerStep > 1) {
          blockedLocation = null;
          setShowExitConfirm(true);
          return false;
        }
      }
      return true;
    });

    window.__historyUnblock = unblock;
    window.__blockedLocation = () => blockedLocation;
    window.__savedSearch = () => savedSearch;

    return () => {
      unblock();
      window.__historyUnblock = null;
      window.__blockedLocation = null;
      window.__savedSearch = null;
    };
  }, [history, registerStep]);

  const handleExitConfirm = () => {
    setShowExitConfirm(false);

    LegalEntityRegistrationStorage.clearAll();

    if (window.__historyUnblock) {
      window.__historyUnblock();
    }

    const blockedLocation = window.__blockedLocation
      ? window.__blockedLocation()
      : null;
    if (blockedLocation) {
      history.push(blockedLocation.pathname);
    } else {
      history.push({
        search: qs.stringify({ ...initialParams, step: 1 }),
      });
    }
  };

  const handleExitCancel = () => {
    setShowExitConfirm(false);
    const savedSearch = window.__savedSearch ? window.__savedSearch() : null;
    if (savedSearch) {
      history.push({
        search: savedSearch,
      });
    }
  };

  const nextStep = (data) => {
    const nextStep = registerStep + 1;
    if (data?.traceId) {
      setTraceId(data.traceId);
      LegalEntityRegistrationStorage.setTraceId(data.traceId);
    }
    if (data?.requiresPosExemption !== undefined) {
      setRequiresPosExemption(data.requiresPosExemption);
      LegalEntityRegistrationStorage.setRequiresPosExemption(data.requiresPosExemption);
    }
    if (data?.inputRut) {
      setStepOneFields({
        inputRut: data.inputRut,
        inputName: data.inputName,
        inputMcc: data.inputMcc,
        inputBusinessNameTypes: data.inputBusinessNameTypes,
      });
    }
    if (data?.email || data?.phone || data?.countryId) {
      const newFormData = {
        data: data?.data || formData.data,
        email: data?.email || formData.email,
        phone: data?.phone || formData.phone,
        countryId: data?.countryId || formData.countryId,
      };
      setFormData(newFormData);
      LegalEntityRegistrationStorage.setStep1Data(newFormData);
    }
    updateUrlWithStep(nextStep, data?.data, data?.requiresPosExemption);
  };

  const prevStep = (data) => {
    updateUrlWithStep(registerStep - 1);
  };

  const renderStep = () => {
    switch (registerStep) {
      case 1:
        return <Disclaimer history={history} nextStep={nextStep} />;
      case 2:
        return (
          <StepOne
            history={history}
            nextStep={nextStep}
            code={affiliationCode}
          />
        );
      case 3:
        return (
          <StepTwo
            history={history}
            nextStep={nextStep}
            prevStep={prevStep}
            stepOneFields={stepOneFields}
            code={affiliationCode}
          />
        );
      case 4:
        return (
          <StepThree
            history={history}
            nextStep={nextStep}
            prevStep={prevStep}
            traceId={traceId}
            stepOneData={formData.data}
            stepOneEmail={formData.email}
          />
        );
      case 5:
        return (
          <StepFour
            history={history}
            nextStep={nextStep}
            prevStep={prevStep}
            stepOneData={formData.data}
          />
        );
      case 6:
        return (
          <StepFive
            history={history}
            nextStep={nextStep}
            prevStep={prevStep}
            traceId={traceId}
            stepOneData={formData.data}
            stepOnePhone={formData.phone}
            stepOnePhoneCountryId={formData.countryId}
          />
        );
      case 7:
        return (
          <StepSix
            history={history}
            nextStep={nextStep}
            prevStep={prevStep}
            traceId={traceId}
            stepOneData={formData.data}
          />
        );
      case 8:
        return (
          <StepSeven
            history={history}
            nextStep={nextStep}
            prevStep={prevStep}
            traceId={traceId}
            stepOneData={formData.data}
            requiresPosExemption={requiresPosExemption}
          />
        );
      case 9:
        return (
          <StepEight
            history={history}
            nextStep={nextStep}
            prevStep={prevStep}
            traceId={traceId}
            stepOneData={formData.data}
          />
        );
      case 10:
        return (
          <StepNine
            history={history}
            nextStep={nextStep}
            prevStep={prevStep}
            traceId={traceId}
            stepOneData={formData.data}
          />
        );
      case 11:
        return (
          <StepTen
            history={history}
            nextStep={nextStep}
            prevStep={prevStep}
            traceId={traceId}
            stepOneData={formData.data}
          />
        );
      case 12:
        return (
          <StepEleven
            history={history}
            nextStep={nextStep}
            prevStep={prevStep}
            stepOneData={formData.data}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Page
      withHeader={false}
      withSideMenu={false}
      withBanner={false}
      withBoxBanner={false}
      showBranchWithNoPaymentMethodWarning={false}>
      <div className={classes.container}>
        <div className={clsx(classes.contentContainer)}>
          <div className={clsx(classes.contentWrapper)}>
            <div className={classes.brandContainer}>
              <img
                src={appLogo}
                className={classes.brandLogo}
                alt="Handy Logo"
              />
              <div className={classes.brandSeparator} />
              <div className={classes.brandTitle}>
                {i18n.get("LegalEntityRegistration.PageTitle")}
              </div>
            </div>
          </div>
          {renderStep()}
        </div>
        <div className={classes.helpContainer} onClick={_toggleShowHelp}>
          {i18n.get("LegalEntityRegistration.UserHelpTitle")}
          <img src={HelpIcon} className={classes.helpIcon} alt="ayuda" />
        </div>
      </div>
      <Confirm
        open={showHelp}
        onClose={_toggleShowHelp}
        title={i18n.get("LegalEntityRegistration.UserHelpDialogTitle")}
        text={i18n.get("LegalEntityRegistration.UserHelpDialogText")}
        confirmText={i18n.get("LegalEntityRegistration.UserHelpDialogButton")}
        onConfirm={_toggleShowHelp}
      />
      <Confirm
        open={showExitConfirm}
        onClose={handleExitCancel}
        title={i18n.get("LegalEntityRegistration.ExitConfirmDialogTitle")}
        text={i18n.get("LegalEntityRegistration.ExitConfirmDialogText")}
        confirmText={i18n.get(
          "LegalEntityRegistration.ExitConfirmDialogConfirmButton",
        )}
        cancelText={i18n.get(
          "LegalEntityRegistration.ExitConfirmDialogCancelButton",
        )}
        onConfirm={handleExitConfirm}
        onCancel={handleExitCancel}
      />
    </Page>
  );
};
LegalEntityRegistrationScreen.id = "com.Handy.LegalEntityRegistration";
export default LegalEntityRegistrationScreen;
